import { useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";

import { useCustomTheme } from "../hooks/useCustomTheme";
import { Icon } from "./Icon";
import {
  LabelText,
  useRequiredControlProps,
  useRequiredLabel,
} from "./LabelText";
import { ThemedText } from "./ThemedText";

export type CheckboxOption<V = string> = {
  label: string;
  value: V;
  disabled?: boolean;
  /** This box must be ticked, unlike a required group's "at least one". */
  required?: boolean;
};

export type CheckboxGroupProps<V = string> = {
  options: CheckboxOption<V>[];
  values: V[];
  onChange: (values: V[]) => void;
  label?: string;
  error?: string | false;
  disabled?: boolean;
  required?: boolean;
  testID?: string;
  style?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  optionStyle?: StyleProp<ViewStyle>;
  optionLabelStyle?: StyleProp<TextStyle>;
};

type WebKeyDownEvent = {
  nativeEvent?: { key?: string };
  preventDefault?: () => void;
};

const ICON_SIZE = 24;
const LABEL_GAP = 8;
const OPTION_GAP = 16;
const OPTION_MIN_HEIGHT = 44;

export function CheckboxGroup<V = string>({
  options,
  values,
  onChange,
  label,
  error,
  disabled = false,
  required = false,
  testID,
  style,
  labelStyle,
  optionStyle,
  optionLabelStyle,
}: CheckboxGroupProps<V>) {
  const { colors } = useCustomTheme();
  const requiredLabel = useRequiredLabel();
  const requiredControlProps = useRequiredControlProps();
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [focusedOption, setFocusedOption] = useState<string | null>(null);

  const toggle = (option: CheckboxOption<V>) => {
    if (disabled || option.disabled) return;
    const checked = values.includes(option.value);
    const next = checked
      ? values.filter((value) => value !== option.value)
      : [...values, option.value];
    onChange(
      options.map(({ value }) => value).filter((value) => next.includes(value)),
    );
  };

  return (
    <View
      testID={testID}
      style={[styles.group, style]}
      role="group"
      accessibilityLabel={
        label && required && !disabled && Platform.OS === "web"
          ? requiredLabel(label)
          : label
      }
    >
      {label ? (
        <LabelText
          style={[{ color: colors.text }, labelStyle]}
          required={required}
          disabled={disabled}
        >
          {label}
        </LabelText>
      ) : null}

      {options.map((option) => {
        const optionKey = String(option.value);
        const isDisabled = disabled || option.disabled === true;
        const checked = values.includes(option.value);
        const highlighted =
          !isDisabled &&
          (hoveredOption === optionKey || focusedOption === optionKey);
        const contentColor = isDisabled ? colors.muted : colors.text;
        const iconColor = isDisabled
          ? colors.muted
          : highlighted
            ? colors.palette.spinach40
            : colors.palette.spinach30;

        const keyboardProps = {
          onKeyDown: (event: WebKeyDownEvent) => {
            if (event.nativeEvent?.key !== " ") return;
            event.preventDefault?.();
            toggle(option);
          },
        } as any;

        return (
          <Pressable
            key={optionKey}
            testID={testID ? `${testID}-option-${optionKey}` : undefined}
            accessibilityRole="checkbox"
            {...requiredControlProps(
              option.label,
              option.required === true && !isDisabled,
            )}
            aria-checked={checked}
            disabled={isDisabled}
            onPress={() => toggle(option)}
            onHoverIn={() => setHoveredOption(optionKey)}
            onHoverOut={() =>
              setHoveredOption((current) =>
                current === optionKey ? null : current,
              )
            }
            onFocus={() => setFocusedOption(optionKey)}
            onBlur={() =>
              setFocusedOption((current) =>
                current === optionKey ? null : current,
              )
            }
            {...keyboardProps}
            style={[
              StyleSheet.compose(styles.option, optionStyle),
              Platform.OS === "web"
                ? ({ outlineStyle: "none" } as any)
                : undefined,
              Platform.OS === "web" && isDisabled
                ? ({ cursor: "not-allowed" } as any)
                : undefined,
            ]}
          >
            <Icon
              iconSet="material-community"
              icon={checked ? "checkbox-marked" : "checkbox-blank-outline"}
              size={ICON_SIZE}
              color={iconColor}
            />
            <ThemedText
              style={[
                styles.optionLabel,
                { color: contentColor },
                optionLabelStyle,
              ]}
            >
              {option.label}
            </ThemedText>
          </Pressable>
        );
      })}

      {error ? (
        <ThemedText
          testID={testID ? `${testID}-error` : undefined}
          accessibilityLiveRegion="polite"
          role="alert"
          style={[styles.error, { color: colors.danger }]}
        >
          {error}
        </ThemedText>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  group: {
    width: "100%",
    gap: OPTION_GAP,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    gap: LABEL_GAP,
    minHeight: OPTION_MIN_HEIGHT,
  },
  optionLabel: {
    flexShrink: 1,
  },
  error: {
    fontSize: 12,
    lineHeight: 14.4,
  },
});
